/**
 * CHANGE 4, the half the brief is emphatic about: "make the remaining attempts
 * and their value VISIBLE before the player commits, so it is an informed
 * choice."
 *
 * So this is on screen from the moment the pinpoint opens, not after the first
 * miss. Three pips, the leftmost lit; under them, what THIS tap is worth if it
 * lands right now. Miss and a pip goes out, the number drops, and the drop is
 * animated so it is felt rather than merely displayed.
 *
 * THE NUMBER IS LIVE, and computing it is the only interesting thing here. The
 * award is base plus a speed bonus that decays with latency, all scaled by the
 * attempt multiplier — so what the next tap pays falls continuously, and a
 * figure frozen at the last server frame would be a lie within a second. It is
 * recomputed against server time each frame with `pinpointAward`, which is the
 * SAME function the server scores with (invariant 6). Nothing is predicted
 * here that the server will not confirm.
 *
 * Driven by requestAnimationFrame writing straight to the DOM, like
 * `CountdownRing` and for the same reason: a 60fps number costs zero React
 * renders.
 */

import { useEffect, useRef } from 'react';
import { pinpointAttemptMultiplier, pinpointAward } from '@geogame/shared';
import type { PinpointAttemptPolicy } from '@geogame/shared';

export interface PinpointAttemptsProps {
  readonly policy: PinpointAttemptPolicy;
  /** Attempts already spent. 0 before the first tap. */
  readonly spent: number;
  /** Server instant the rung opened; the speed bonus decays from here. */
  readonly startAtServerTs: number | null;
  readonly serverNow: () => number;
  /** True while a tap is in flight — the number freezes rather than lying. */
  readonly pending: boolean;
}

export function PinpointAttempts({
  policy,
  spent,
  startAtServerTs,
  serverNow,
  pending,
}: PinpointAttemptsProps): React.JSX.Element | null {
  const valueRef = useRef<HTMLSpanElement | null>(null);
  const attempt = spent + 1;
  const multiplier = pinpointAttemptMultiplier(policy, attempt);
  const exhausted = multiplier <= 0;

  useEffect(() => {
    if (exhausted || pending) return undefined;
    let raf = 0;
    const frame = (): void => {
      raf = window.requestAnimationFrame(frame);
      const el = valueRef.current;
      if (!el) return;
      const latency = startAtServerTs === null ? 0 : Math.max(0, serverNow() - startAtServerTs);
      el.textContent = String(pinpointAward(latency, multiplier));
    };
    raf = window.requestAnimationFrame(frame);
    return () => window.cancelAnimationFrame(raf);
  }, [exhausted, multiplier, pending, serverNow, startAtServerTs]);

  // A tier with a single attempt has nothing to report and no choice to inform.
  if (policy.maxAttempts <= 1) return null;

  return (
    <div className="gg-attempts" data-exhausted={exhausted ? 'true' : 'false'}>
      <div className="gg-attempt-pips" aria-hidden="true">
        {Array.from({ length: policy.maxAttempts }, (_, i) => (
          <span
            key={i}
            className="gg-attempt-pip"
            data-state={i < spent ? 'spent' : i === spent ? 'live' : 'held'}
          />
        ))}
      </div>
      <p className="gg-attempt-line">
        {exhausted ? (
          <span className="gg-attempt-gone">No attempts left</span>
        ) : (
          <>
            <span className="gg-attempt-label">
              Try {attempt} of {policy.maxAttempts} · worth
            </span>{' '}
            {/*
              Keyed on the attempt so React replaces the node on a miss and the
              drop animation replays. Without the key it is the same element
              with new text and the change goes unnoticed.
            */}
            <span
              key={attempt}
              ref={valueRef}
              className="gg-attempt-value tabular-nums"
              data-drop={spent > 0 ? 'true' : 'false'}
            />
            {spent > 0 ? (
              <span className="gg-attempt-cut">
                {' '}
                · {Math.round(multiplier * 100)}% after {spent === 1 ? 'a miss' : `${spent} misses`}
              </span>
            ) : null}
          </>
        )}
      </p>
    </div>
  );
}
